<template>
<AppPopup
	:modelValue="open"
	name="Employee Profile"
	:noneOverflowAuto="true"
	min-width="900px"
	max-width="1200px"
	@after-close="confirmHasChangeAndClose"
>
	<div class="p-6 flex flex-col gap-4 h-[70vh] overflow-hidden">

		<!--Profile-->
		<div class="border-b-1 border-border-basic-1 shrink-0">
			<div class="flex items-center gap-2">
				<StatusTag :statusCode="employee.employeeStatus"/>
				<h3 class="font-heading-sm-semibold"> {{ employee.employeeName }} </h3>
			</div>
			<div class="font-body-sm-regular text-content-basic-2 my-2">{{ employee.departmentName || '-' }} / {{ employee.jobTitleName || '-' }}</div>
		</div>

		<div class="flex min-h-0 h-full gap-6 w-full flex-shrink-1">
			<!--Side Menu-->
			<div class="w-[11rem] h-full shrink-0 justify-self-stretch bg-background-basic-3 rounded-3 p-2 font-body-sm-regular">
				<a
					@click="showInfo('PROFILE')"
					href="javascript:void(0)"
					class="rounded-3 flex items-center h-[2.75rem] w-full px-3 text-content-basic-2"
					:class="{
							'bg-background-basic-1 shadow-card text-primary-5 font-semibold': PopupProfileInfo,
					}"
				>
					<span class="flex-1">Profile</span>
				</a>
				<a
					v-if="$appUtil.checkPermission('CONTRACT_INFO_VIEW') || $appUtil.checkPermission('CONTRACT_INFO_EDIT')"
					@click="showInfo('CONTRACT')"
					href="javascript:void(0)"
					class="rounded-3 flex items-center h-[2.75rem] w-full px-3 text-content-basic-2"
					:class="{
							'bg-background-basic-1 shadow-card text-primary-5 font-semibold': PopupContactInfo,
					}"
				>
					<span class="flex-1">Contract Info</span>
				</a>
				<a
					v-if="$appUtil.checkPermission('SALARY_INFO_VIEW') || $appUtil.checkPermission('SALARY_INFO_EDIT')"
					@click="showInfo('SALARY')"
					href="javascript:void(0)"
					class="rounded-3 flex items-center h-[2.75rem] w-full px-3 text-content-basic-2"
					:class="{
							'bg-background-basic-1 shadow-card text-primary-5 font-semibold': PopupSalaryInfo,
					}"
				>
					<span class="flex-1">Salary Info</span>
				</a>
				<a
					v-if="$appUtil.checkPermission('PREVIOUS_EMPLOYER_INFO_VIEW') || $appUtil.checkPermission('PREVIOUS_EMPLOYER_INFO_EDIT')"
					@click="showInfo('SALARY_PREVIOUS')"
					href="javascript:void(0)"
					class="rounded-3 flex items-center h-[2.75rem] w-full px-3 text-content-basic-2"
					:class="{
							'bg-background-basic-1 shadow-card text-primary-5 font-semibold': PopupSalaryInfoPrevious,
					}"
				>
					<span class="flex-1">Previous Employer</span>
				</a>
			</div>

			<!--content - Profile-->
			<div v-if="PopupProfileInfo" class="w-full overflow-hidden" style="max-width:calc(100% - 12.5rem);">
				<div class="border-b border-border-basic-2 bg-transparent flex items-end gap-8 mt-1 mb-2 bg-white">
					<template v-for="tab in tabs" v-bind:key="tab.name">
						<a
							v-show="tab.permission"
							class="border-b-4 pb-1.5 font-body-sm-regular text-content-basic-3 transition-all duration-500 max-w-[10rem] text-center"
							:class="{
								'border-transparent': selectedTab !== tab,
								'border-primary-5 text-primary-5 font-semibold': selectedTab === tab,
								'text-content-basic-1': selectedTab === tab,
							}"
							href="javascript:void(0)"
							@click="clickTab(tab)"
						>
							{{ tab.text }}
						</a>
					</template>
				</div>


				<template v-if="noInfoPermission">
					<div class="flex flex-col items-center pb-4">
						<EmojiSadIcon class="h-10 w-10"/>
						<div class="font-heading-sm-semibold text-accent-red-3" > Access Restricted </div>
						<div class="text-content-basic-2 "> 
							You don't have permission to view information of employee.
						</div>
					</div>
				</template>
				<template v-else > 
					<div class="h-full overflow-auto pb-[280px]">
						<TabJobInfo v-if="tabs[0] === selectedTab"
							@afterSave="getEmployeeData"
							@getHistory="getEmployeeStatusHistoryDataAll(selectedEmployeeSeq)"
							@editing-change="hasInfoChange=$event"
							:employeeStatusHistoryList="employeeStatusHistoryList"
							:selectedEmployeeSeq="selectedEmployeeSeq"
						/>
						<TabBasicInfo v-if="tabs[1] === selectedTab"
							@afterSave="getEmployeeData"
							@editing-change="hasInfoChange=$event"
							:selectedEmployeeSeq="selectedEmployeeSeq"
						/>
						<TabEducation v-if="tabs[2] === selectedTab" :selectedEmployeeSeq="selectedEmployeeSeq"/>
						<TabCareer v-if="tabs[3] === selectedTab" :selectedEmployeeSeq="selectedEmployeeSeq"/>
						<TabDocument v-if="tabs[4] === selectedTab" :selectedEmployeeSeq="selectedEmployeeSeq"/>
					</div>
				</template>
				
			</div>


			<div v-if="PopupContactInfo" class="overflow-auto w-full relative">
				<PopupContactInfo
					:employeeData="employee"
					@afterSave="getEmployeeData"
				/>
			</div>
			<div v-if="PopupSalaryInfo" class="overflow-auto w-full relative">
				<PopupSalaryInfo
					:employeeData="employee"
					@refresh-employee-data="getEmployeeData"
				/>
			</div>
			<div v-if="PopupSalaryInfoPrevious" class="overflow-auto w-full relative">
				<PopupSalaryInfoPrevious
					:employeeData="employee"/>
			</div>
		</div>
	</div>
</AppPopup>
<Teleport to="body">
	<ElementsModal2Button
		v-model="showUnsavedModal"
		:hasAlertIcon="true"
		:modalTitle="'You have unsaved changes.\nAre you sure you want to leave?'"
		buttonText="Leave"
		btnColor="RED"
		@afterClick="confirmLeave"
	/>
</Teleport>
</template>

<script>
import PopupContactInfo from "@/pages/employees/employees/popups/PopupContactInfo.vue";
import PopupSalaryInfo from "@/pages/employees/employees/popups/PopupSalaryInfo.vue";
import PopupSalaryInfoPrevious from "@/pages/employees/employees/popups/PopupSalaryInfoPrevious.vue"
import TabJobInfo from "@/pages/employees/employees/popups/tabs/TabJobInfo.vue";
import TabBasicInfo from "@/pages/employees/employees/popups/tabs/TabBasicInfo.vue";
import TabEducation from "@/pages/employees/employees/popups/tabs/TabEducation.vue";
import TabCareer from "@/pages/employees/employees/popups/tabs/TabCareer.vue";
import TabDocument from "@/pages/employees/employees/popups/tabs/TabDocument";
import StatusTag from "@/components/employee/status-tag/employee-status-tag.vue"
import { EmojiSadIcon } from "@heroicons/vue/outline";
import moment from "moment";
import AppPopup from "@/components/AppPopup.vue";

export default {
	components: {
        PopupContactInfo,
        PopupSalaryInfo,
		PopupSalaryInfoPrevious,
		TabJobInfo,
		TabBasicInfo,
		TabEducation,
		TabCareer,
        TabDocument,
		EmojiSadIcon,
        StatusTag,
		AppPopup
	},
	props: {
		open : { type : Boolean },
		selectedEmployeeSeq:String,
	},
    emits: ["after-close"],
	data() {
		return {
            notGrayscale: 'grayscale(0%)',
            grayscale: 'grayscale(100%)',
            hasInfoChange: false,
            showUnsavedModal: false,
            pendingAction: null,
            employeeStatusHistoryList : [],
            userSeq: null,
            employeeStatus: null,
            contractInfoEmployeeList: [],
            employeeSeq: null,
			selectedTab: {},
            PopupContactInfo: false,
            PopupSalaryInfo: false,
            PopupProfileInfo: true,
			PopupSalaryInfoPrevious: false,
			tabs: [
				{
					name: "jobInfo",
					text: "Job Info",
					permissionKey : 'BASIC_INFO',
					permission : false,
				},
				{
					name: "basicInfo",
					text: "Basic Info",
					permissionKey : 'BASIC_INFO',
					permission : false,
				},
				{
					name: "education",
					text: "Education",
					permissionKey : 'BASIC_INFO',
					permission : false,
				},
				{
					name: "career",
					text: "Work Experience/Certificate",
					permissionKey : 'BASIC_INFO',
					permission : false,
				},
				{
					name: "document",
					text: "Employee Documents",
					permissionKey : 'EMPLOYEE_DOCUMENTS_INFO',
					permission : false,
				},
			],
			employee: {},
			noInfoPermission : false,

		}; //return
	}, //data
	methods: {
        moment: moment,
        // 계약 정보가 30일 이하인 데이터 리스트를 불러온다
        getContractInfoEmployeeList(userSeq){
            const self = this;
            const url = self.$api("uri", `get-employee-contract-info-history-one`).replace("{employeeSeq}", encodeURIComponent(userSeq));
            self.$axios
                .get(url)
                .then((res) => {
                    self.contractInfoEmployeeList = res.data.data.list;
                })
                .catch((err) => {
                    console.error('err : ',err);
                })
        },
        showInfo(infoName){
            const self = this;
            if (self.hasInfoChange && self.PopupProfileInfo) {
                self.pendingAction = () => self._doShowInfo(infoName);
                self.showUnsavedModal = true;
                return;
            }
            self._doShowInfo(infoName);
        },
        _doShowInfo(infoName) {
            const self = this;
            self.PopupProfileInfo = false;
            self.PopupContactInfo = false;
            self.PopupSalaryInfo = false;
			self.PopupSalaryInfoPrevious = false;

            switch(infoName) {
                case 'PROFILE' :
                    self.PopupProfileInfo = true;
                    break;
                case 'CONTRACT' :
                    self.PopupContactInfo = true;
                    break;
                case 'SALARY' :
                    self.PopupSalaryInfo = true;
                    break;
				case 'SALARY_PREVIOUS' :
					self.PopupSalaryInfoPrevious = true;
					break;
                default :
                    self.PopupProfileInfo = true;
            }
        },
		clickTab(tab) {
			const self = this;
			if (self.noInfoPermission) return;
			if (self.hasInfoChange) {
				self.pendingAction = () => { self.selectedTab = tab; };
				self.showUnsavedModal = true;
				return;
			}
			self.selectedTab = tab;
		},
        confirmLeave() {
            if (this.pendingAction) {
                this.pendingAction();
                this.pendingAction = null;
            }
            this.hasInfoChange = false;
            this.showUnsavedModal = false;
        },
		getEmployeeData() {
			const self = this;
			const url = self.$api("uri", `get-employee-basic-info`).replace("{employeeSeq}", encodeURIComponent(self.selectedEmployeeSeq));
			self.$axios.get(url)
				.then((res) => {
					self.employee = res.data.data;

                    self.userSeq = self.employee.employeeSeq;
					self.getContractInfoEmployeeList(self.userSeq);
				})
				.catch((err) => {
					console.log(err);
				});
		},
        getEmployeeStatusHistoryDataAll(userSeq) {
			const self = this;
			const url = self.$api("uri", `get-employee-status-history-all`).replace("{employeeSeq}", encodeURIComponent(userSeq));
            self.$axios.get(url)
				.then((res) => {
					self.employeeStatusHistoryList = res.data.data.list;
                })
				.catch((err) => {
					console.log(err);
				});
		},
		confirmHasChangeAndClose(){
			const self = this;
            if (self.hasInfoChange && self.PopupProfileInfo) {
                self.pendingAction = () => self.$emit('after-close');
                self.showUnsavedModal = true;
                return;
            }
			self.pendingAction = null;
			self.hasInfoChange = false;
			self.$emit('after-close');
		}
	},
	mounted(){
        const self =this;
		self.getEmployeeData();
		self.getContractInfoEmployeeList(self.userSeq);

		const tab = this.tabs.find(tab => tab.permission);
		this.clickTab(tab);
	},
	beforeMount(){
		const self = this;

		const permission_menu = [
			"BASIC_INFO_EDIT",
			"BASIC_INFO_VIEW",
            "EMPLOYEE_DOCUMENTS_INFO_VIEW",
		]

		// 권한 있나 찾음 
		const infoPermissions = permission_menu.filter( self.$appUtil.checkPermission );
		self.noInfoPermission = infoPermissions.length == 0;
		// 권한 있으면 각 탭 볼수있는지 권한 넣기
		if ( ! self.noInfoPermission ) {
			self.tabs.forEach( tab => {
				const re = new RegExp(`${tab.permissionKey}.*`)
				tab.permission = infoPermissions.some( pms => re.test(pms) )
			})
		}
	}
};
</script>
